import React from 'react';

import { Login, Logout, Person } from '@mui/icons-material';
import type { CardProps } from '@mui/material';
import { Card, CardActionArea, CardContent, Checkbox, Chip, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { Link, useMatch } from 'react-router-dom';
import { EnlargeableAvatar } from '../../../../components';
import { createScopedI18n } from '../../../../i18n/i18n';
import { stopEventPropagation } from '../../../../utils/libs';
import { AttendancesPageContext } from '../attendances_page_context';
import useAttendanceLocation from '../hooks/use_attendance_location';
import StatusIcon from './status_icon';

import type { JobHiringAttendance } from '../type';

const attendancesPageI18n = createScopedI18n('pages.attendances');

const ICON_CONTAINER_SIZE = 30;
const PHOTO_CARD_WIDTH = 120;
const PLACEHOLDER_ICON_SIZE = 40;

export type AttendanceCardProps = {
  attendance: JobHiringAttendance;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  disableSelect?: boolean;
  navigateTo?: string;
  replaceWhenNavigate?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>, attendance: JobHiringAttendance) => void;
} & CardProps;

type AttendanceCardInnerProps = Omit<AttendanceCardProps, 'selected'> & {
  isSelected?: boolean;
  onSelectAttendance: (attendanceId: string, selected: boolean) => void;
  setCurrentAttendanceId: React.Dispatch<React.SetStateAction<string | null>>;
};

export const AttendanceCard = React.memo(
  ({
    attendance,
    isSelected,
    disableSelect,
    onSelectAttendance,
    setCurrentAttendanceId,
    onSelectChange,
    navigateTo,
    replaceWhenNavigate,
    onClick,
    ...cardProps
  }: AttendanceCardInnerProps) => {
    const theme = useTheme();

    const attendanceIdPathMatch = useMatch('/attendances/:id');
    const isAttendanceIdPathOpen = React.useMemo(
      () => !!attendanceIdPathMatch?.params.id,
      [attendanceIdPathMatch?.params.id],
    );

    const isCurrentlyOpen = React.useMemo(
      () => isAttendanceIdPathOpen && attendanceIdPathMatch?.params.id === attendance.id,
      [isAttendanceIdPathOpen, attendanceIdPathMatch?.params.id, attendance.id],
    );
    const isReplace = React.useMemo(
      () => replaceWhenNavigate ?? isAttendanceIdPathOpen,
      [replaceWhenNavigate, isAttendanceIdPathOpen],
    );

    const student = attendance.jobHiring?.student;
    const { isStartOperationInRange, isEndOperationInRange } = useAttendanceLocation(attendance);
    const isInRange = isStartOperationInRange && isEndOperationInRange;

    const onSelectHandler = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        onSelectChange?.(event.target.checked);
        onSelectAttendance(attendance.id, event.target.checked);
      },
      [onSelectChange, onSelectAttendance, attendance.id],
    );

    const onClickHandler = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        onClick?.(event, attendance);
        setCurrentAttendanceId(attendance.id);
      },
      [onClick, setCurrentAttendanceId, attendance.id],
    );

    return (
      <Card {...cardProps}>
        <CardActionArea component={Link} to={navigateTo ?? attendance.id} replace={isReplace} onClick={onClickHandler}>
          <CardContent>
            <Stack gap={1}>
              <Stack flexDirection="row" gap={1}>
                <Stack flex={1}>
                  <Typography variant="subtitle1">
                    {attendancesPageI18n('temp_name', { name: student?.nickName })}
                  </Typography>
                  <Typography variant="caption">{student?.nameWithFallback}</Typography>
                </Stack>

                <Stack alignItems="flex-end">
                  <Stack
                    width={ICON_CONTAINER_SIZE}
                    height={ICON_CONTAINER_SIZE}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {attendance.status === 'pending' ? (
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={disableSelect}
                        onChange={onSelectHandler}
                        onClick={stopEventPropagation}
                        onMouseDown={stopEventPropagation}
                      />
                    ) : (
                      <StatusIcon status={attendance.status} />
                    )}
                  </Stack>

                  {isInRange ? null : (
                    <Chip
                      size="small"
                      label={attendancesPageI18n('out_of_range')}
                      color="error"
                      variant="outlined"
                      sx={{ zoom: 0.8 }}
                    />
                  )}
                </Stack>
              </Stack>

              <Stack flexDirection="row" gap={1} marginTop={1} alignItems="center">
                <Stack flex={1} gap={1} width={PHOTO_CARD_WIDTH}>
                  <Stack flexDirection="row" gap={0.75} paddingLeft={1} alignItems="center">
                    <Login fontSize="small" />
                    <Typography variant="body2">
                      {attendance.startOperationTime
                        ? dayjs(attendance.startOperationTime).format('HH:mm')
                        : attendancesPageI18n('clock_in_time')}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Card variant="outlined" sx={{ aspectRatio: 1 }}>
                      {attendance.startOperationPhoto ? (
                        <EnlargeableAvatar src={attendance.startOperationPhoto} fullWidth fullHeight noBorderRadius />
                      ) : (
                        <Stack width="100%" height="100%" padding={1} justifyContent="center" alignItems="center">
                          <Person sx={{ color: theme.palette.text.secondary, fontSize: PLACEHOLDER_ICON_SIZE }} />
                          <Typography variant="caption" color={theme.palette.text.secondary} textAlign="center">
                            {attendancesPageI18n('clock_in_out_photo_placeholder')}
                            <br />
                            {attendancesPageI18n('clock_in_photo_caption')}
                          </Typography>
                        </Stack>
                      )}
                    </Card>
                  </Stack>
                </Stack>

                <Stack alignItems="center">
                  <Typography variant="caption">{attendancesPageI18n('break_duration')}</Typography>
                  <Stack
                    width={ICON_CONTAINER_SIZE}
                    height={ICON_CONTAINER_SIZE}
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="50%"
                    bgcolor={theme.palette.divider}
                  >
                    <Typography variant="subtitle1">
                      {!isNil(attendance.breakDurationMinute) ? attendance.breakDurationMinute : '-'}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack flex={1} gap={1} width={PHOTO_CARD_WIDTH}>
                  <Stack flexDirection="row" gap={0.75} paddingLeft={1} alignItems="center">
                    <Logout fontSize="small" />
                    <Typography variant="body2">
                      {attendance.endOperationTime
                        ? dayjs(attendance.endOperationTime).format('HH:mm')
                        : attendancesPageI18n('clock_out_time')}
                    </Typography>
                  </Stack>
                  <Card variant="outlined" sx={{ aspectRatio: 1 }}>
                    {attendance.endOperationPhoto ? (
                      <EnlargeableAvatar src={attendance.endOperationPhoto} fullWidth fullHeight noBorderRadius />
                    ) : (
                      <Stack width="100%" height="100%" padding={1} justifyContent="center" alignItems="center">
                        <Person sx={{ color: theme.palette.text.secondary, fontSize: PLACEHOLDER_ICON_SIZE }} />
                        <Typography variant="caption" color={theme.palette.text.secondary} textAlign="center">
                          {attendancesPageI18n('clock_in_out_photo_placeholder')}
                          <br />
                          {attendancesPageI18n('clock_out_photo_caption')}
                        </Typography>
                      </Stack>
                    )}
                  </Card>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  },
);

const AttendanceCardContainer = ({ attendance, selected, ...rest }: AttendanceCardProps) => {
  const { selectedAttendancesIds, selectAttendance, setCurrentAttendanceId, canSelectAttendance } =
    React.useContext(AttendancesPageContext);
  const isSelected = selected ?? selectedAttendancesIds.has(attendance.id);
  const canSelected = canSelectAttendance(attendance.id);

  return (
    <AttendanceCard
      attendance={attendance}
      isSelected={isSelected}
      disableSelect={!canSelected}
      onSelectAttendance={selectAttendance}
      setCurrentAttendanceId={setCurrentAttendanceId}
      {...rest}
    />
  );
};

export default AttendanceCardContainer;
